import { getClientsForRegions, type RegionClient } from '@/api'
import { useRegionStore } from '@/stores/regionStore'
import type { RegionCode } from '@/config/regions'

/** Clients for the active region view (IN / GCC / both when GLOBAL). */
export function getActiveRegionClients(): RegionClient[] {
  const regions = useRegionStore.getState().activeRegions()
  const clients = getClientsForRegions(regions)
  if (clients.length === 0) {
    throw new Error(
      'No regional API configured. Set VITE_API_URL_IN and/or VITE_API_URL_GCC (or legacy VITE_API_URL).'
    )
  }
  return clients
}

export function tagRowsWithRegion<T extends object>(
  rows: T[],
  region: RegionCode
): Array<T & { region: RegionCode }> {
  return rows.map((row) => ({ ...row, region }))
}

export async function mapRegionClients<T>(
  fn: (rc: RegionClient) => Promise<T>
): Promise<Array<{ region: RegionCode; result: T }>> {
  const clients = getActiveRegionClients()
  const settled = await Promise.allSettled(clients.map(async (rc) => ({ region: rc.region, result: await fn(rc) })))
  const ok: Array<{ region: RegionCode; result: T }> = []
  const errors: string[] = []
  for (const s of settled) {
    if (s.status === 'fulfilled') ok.push(s.value)
    else {
      const msg =
        s.reason && typeof s.reason === 'object' && 'message' in s.reason
          ? String((s.reason as { message?: string }).message)
          : String(s.reason)
      errors.push(msg)
    }
  }
  if (ok.length === 0) {
    throw { message: errors.join('; ') || 'All regional API calls failed' }
  }
  return ok
}
